import { useEffect, useRef, useState } from 'preact/hooks';
import { active, userId, activeToken, showSteps } from '../store/global';
import {
  setShowSteps,
  setUserId,
  chatSessionKey,
  getChatSessionId,
  setChatSessionId,
} from '../lib/auth';
import { streamRun } from '../lib/sse';
import { getSessionRuns } from '../lib/api';
import type { AnyAgnoEvent } from '../types/agno-events';
import type { SessionRunSummary } from '../types/agno-api';
import { ChatMessage } from '../components/ChatMessage';
import { EventTimeline } from '../components/EventTimeline';
import { RunMetrics } from '../components/RunMetrics';
import { SubagentPanel } from '../components/SubagentPanel';

interface SubagentStream {
  agent: string;
  content: string;
}

interface Turn {
  id: string;
  userMessage: string;
  agentName?: string;
  content: string;          // resposta do agente — supervisor consolidado ou subagent
  reasoning: string;
  events: AnyAgnoEvent[];
  subagents: Record<string, SubagentStream>;  // streams de RunContent de cada subagent
  startedAt: number;
  reasoningStartedAt?: number;
  reasoningEndedAt?: number;
  metrics?: {
    inputTokens?: number;
    outputTokens?: number;
    totalTokens?: number;
    timeToFirstToken?: number;
    duration?: number;
    models?: Array<{ id: string; provider: string }>;
  };
  pending: boolean;
  reasoningPending: boolean;
  error?: string;
}

// Recupera o session_id existente pra esta combinação (endpoint, alvo) ou cria
// um novo e persiste. Retorna [sessionId, isNew] — isNew indica se acabou de
// nascer (não vale a pena bater no servidor pra buscar histórico).
function loadOrCreateSession(endpointUrl: string, targetId: string): { id: string; isNew: boolean } {
  const key = chatSessionKey(endpointUrl, targetId);
  const existing = getChatSessionId(key);
  if (existing) return { id: existing, isNew: false };
  const fresh = crypto.randomUUID();
  setChatSessionId(key, fresh);
  return { id: fresh, isNew: true };
}

// Eventos que NÃO vão pra timeline de "passos" — são streaming de conteúdo,
// lifecycle interno ou raciocínio, que aparecem em outros lugares ou seriam
// só ruído. Usado tanto no streaming live (applyEvent) quanto na reconstrução
// do histórico (runsToTurns) pra dar consistência entre live e reload.
const NOISE_EVENTS = new Set<string>([
  'TeamRunContent',
  'RunContent',
  'IntermediateRunContent',
  'TeamRunContentCompleted',
  'RunContentCompleted',
  'TeamRunStarted',
  'TeamRunCompleted',
  'RunStarted',
  'RunCompleted',
  'TeamModelRequestStarted',
  'TeamModelRequestCompleted',
  'ModelRequestStarted',
  'ModelRequestCompleted',
  'ReasoningStarted',
  'ReasoningStep',
  'ReasoningContentDelta',
  'ReasoningCompleted',
]);

// Normaliza created_at — Agno entrega ora número (epoch segundos), ora
// string ISO ("2026-05-24T04:20:43Z"). Retornamos ms epoch sempre.
function normalizeCreatedAt(v: number | string | undefined): number {
  if (typeof v === 'number') return v * 1000;
  if (typeof v === 'string') return new Date(v).getTime();
  return 0;
}

// Extrai a pergunta do humano. Só faz sentido no run do team supervisor
// (que recebe a mensagem original do usuário). Em runs de subagent o
// messages[role=user] é a INSTRUÇÃO emitida pelo supervisor — exibir isso
// como bubble do usuário seria enganoso, então retornamos vazio.
function extractUserMessage(run: SessionRunSummary): string {
  if (run.agent_id && !run.team_id) return '';
  if (typeof run.input === 'string') return run.input;
  if (run.input_content) return run.input_content;
  if (typeof run.input === 'object' && run.input) {
    if (run.input.content) return run.input.content;
    const fromMsgs = run.input.messages?.find((m) => m.role === 'user');
    if (fromMsgs) return fromMsgs.content;
  }
  const fromMsgs = run.messages?.find((m) => m.role === 'user');
  if (fromMsgs) return fromMsgs.content;
  return '';
}

function runsToTurns(runs: SessionRunSummary[]): Turn[] {
  // Indexa runs de subagent por parent_run_id pra reconstruir o bucket
  // `subagents` do turno do team. Esses runs não viram bubbles próprios — só
  // alimentam o painel "passos" quando o usuário liga o toggle.
  const subagentByParent: Record<string, SessionRunSummary[]> = {};
  for (const run of runs) {
    if (run.agent_id && !run.team_id && run.parent_run_id) {
      (subagentByParent[run.parent_run_id] ??= []).push(run);
    }
  }

  return runs
    .filter((run) => !(run.agent_id && !run.team_id))
    .slice()
    .sort((a, b) => normalizeCreatedAt(a.created_at) - normalizeCreatedAt(b.created_at))
    .map((run) => {
      // Eventos do servidor já vêm completos — filtramos a mesma lista de
      // ruído que aplicamos no streaming live pra "passos" mostrar tool
      // calls, delegations e métricas, sem poluir com lifecycle/conteúdo.
      const rawEvents = (run.events || []) as unknown as AnyAgnoEvent[];
      const events = rawEvents.filter((e) => !NOISE_EVENTS.has(e.event));

      // Bucket de subagents: cada filho do run vira uma entrada chaveada
      // pelo agent_id. Mesmo formato do live (alimentado por RunContent).
      const subagents: Record<string, SubagentStream> = {};
      for (const child of subagentByParent[run.run_id] || []) {
        const agent = child.agent_id || 'subagent';
        subagents[agent] = { agent, content: child.content || '' };
      }

      return {
        id: run.run_id,
        userMessage: extractUserMessage(run),
        agentName: run.team_id || run.agent_id || undefined,
        content: run.content || '',
        reasoning: '',
        events,
        subagents,
        startedAt: normalizeCreatedAt(run.created_at),
        pending: false,
        reasoningPending: false,
      };
    });
}

export function Chat() {
  const [turns, setTurns] = useState<Turn[]>([]);
  const [input, setInput] = useState('');
  const [sessionId, setSessionId] = useState<string>(() => crypto.randomUUID());
  const [loadingHistory, setLoadingHistory] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  // Sincroniza sessionId + histórico sempre que o alvo (endpoint, agente) muda.
  // A cada troca: lê o session_id do storage (ou cria), e busca os runs do
  // servidor pra reconstruir os turnos. "Nova conversa" reseta tudo via storage.
  useEffect(() => {
    const ref = active.value;
    if (!ref || !activeToken.value) {
      setTurns([]);
      return;
    }
    const { id, isNew } = loadOrCreateSession(ref.endpointUrl, ref.targetId);
    setSessionId(id);
    if (isNew) {
      setTurns([]);
      return;
    }
    let cancelled = false;
    setLoadingHistory(true);
    getSessionRuns(id)
      .then((runs) => {
        if (cancelled) return;
        setTurns(runsToTurns(runs));
      })
      .catch((err) => {
        // 404 = sessão ainda não existe no servidor (criada localmente, sem
        // primeira mensagem). Trata como vazia silenciosamente.
        if (cancelled) return;
        const status = (err as { status?: number }).status;
        if (status !== 404) console.error('Falha ao carregar histórico:', err);
        setTurns([]);
      })
      .finally(() => !cancelled && setLoadingHistory(false));
    return () => {
      cancelled = true;
    };
  }, [active.value?.endpointUrl, active.value?.targetId, activeToken.value]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [turns]);

  const newConversation = () => {
    abortRef.current?.abort();
    setTurns([]);
    const ref = active.value;
    const fresh = crypto.randomUUID();
    if (ref) setChatSessionId(chatSessionKey(ref.endpointUrl, ref.targetId), fresh);
    setSessionId(fresh);
  };

  const stop = () => abortRef.current?.abort();

  const send = async () => {
    const msg = input.trim();
    if (!msg || !active.value || !activeToken.value) return;
    setInput('');

    const id = crypto.randomUUID();
    const turn: Turn = {
      id,
      userMessage: msg,
      content: '',
      reasoning: '',
      events: [],
      subagents: {},
      startedAt: Date.now(),
      pending: true,
      reasoningPending: false,
    };
    setTurns((p) => [...p, turn]);

    const abort = new AbortController();
    abortRef.current = abort;

    try {
      await streamRun(
        {
          targetId: active.value.targetId,
          isTeam: active.value.isTeam,
          message: msg,
          sessionId,
          userId: userId.value,
          signal: abort.signal,
        },
        {
          onEvent: (ev) => {
            setTurns((p) => p.map((t) => (t.id !== id ? t : applyEvent(t, ev))));
          },
          onComplete: () => {
            setTurns((p) => p.map((t) => (t.id === id ? { ...t, pending: false, reasoningPending: false } : t)));
          },
          onError: (err) => {
            setTurns((p) =>
              p.map((t) =>
                t.id === id
                  ? { ...t, pending: false, reasoningPending: false, error: err.message }
                  : t,
              ),
            );
          },
        },
      );
    } catch (err) {
      setTurns((p) =>
        p.map((t) => (t.id === id ? { ...t, pending: false, error: (err as Error).message } : t)),
      );
    }
  };

  const ready = !!active.value && !!activeToken.value;

  return (
    <div class="flex flex-col h-full">
      {/* Toolbar: ações principais ficam sempre visíveis; toggles colapsam em < sm. */}
      <div class="border-b border-border px-3 sm:px-4 py-2 flex items-center gap-2 sm:gap-3 text-xs flex-wrap">
        <button
          class="inline-flex items-center justify-center gap-2 h-9 sm:h-8 px-4 rounded-md text-sm font-medium bg-background text-foreground border border-input shadow-xs hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:border-ring"
          onClick={newConversation}
        >
          <i class="ph ph-plus text-sm" /> <span>Nova conversa</span>
        </button>

        <div class="text-muted-foreground font-mono hidden sm:block">
          session {sessionId.slice(0, 8)}…
        </div>

        <UserIdEditor />

        <div class="flex-1" />

        <Toggle
          label="passos"
          icon={<i class="ph ph-tree-view text-sm" />}
          value={showSteps.value}
          onChange={(v) => {
            showSteps.value = v;
            setShowSteps(v);
          }}
          title="Mostrar tool calls, delegação e métricas"
        />

        <div class="text-muted-foreground hidden sm:block">{turns.length} turnos</div>
      </div>

      {/* Messages */}
      <div class="flex-1 overflow-y-auto">
        <div class="max-w-4xl mx-auto px-3 sm:px-4 py-4 sm:py-6 space-y-4 sm:space-y-6">
          {turns.length === 0 && (
            <div class="text-center text-muted-foreground py-20">
              {loadingHistory ? (
                <p class="text-sm">Carregando histórico…</p>
              ) : ready ? (
                <p class="text-sm">Nenhuma mensagem nesta sessão. Digite abaixo para começar.</p>
              ) : (
                <p class="text-sm">
                  Selecione um endpoint e um agente na sidebar para começar.
                </p>
              )}
            </div>
          )}
          {turns.map((t) => {
            return (
              <div key={t.id} class="space-y-3">
                {t.userMessage && <ChatMessage role="user" content={t.userMessage} />}

                {/* Steps timeline — toggle independente */}
                {showSteps.value && t.events.length > 0 && (
                  <div class="pl-0 sm:pl-11 max-w-full sm:max-w-3xl">
                    <EventTimeline events={t.events} />
                  </div>
                )}

                {/* Resposta intermediária de cada subagent — colapsável,
                    só aparece se "passos" tá ON. Mostra o que cada member do
                    team falou antes do supervisor consolidar. */}
                {showSteps.value && Object.keys(t.subagents).length > 0 && (
                  <div class="pl-0 sm:pl-11 max-w-full sm:max-w-3xl space-y-1.5">
                    {Object.values(t.subagents).map((s) => (
                      <SubagentPanel key={s.agent} agent={s.agent} content={s.content} />
                    ))}
                  </div>
                )}

                <ChatMessage
                  role="assistant"
                  content={t.content}
                  agentName={t.agentName}
                  pending={t.pending && !t.content}
                />

                {t.metrics && (
                  <div class="pl-0 sm:pl-11">
                    <RunMetrics {...t.metrics} />
                  </div>
                )}

                {t.error && (
                  <div class="pl-0 sm:pl-11 max-w-full sm:max-w-3xl text-xs text-destructive font-mono border-l-2 border-destructive pl-2 break-words">
                    {t.error}
                  </div>
                )}
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>
      </div>

      {/* Composer — sticky bottom, respeita safe-area do iPhone notch. */}
      <div class="border-t border-border bg-background pb-[env(safe-area-inset-bottom)]">
        <div class="max-w-4xl mx-auto px-3 sm:px-4 py-2.5 sm:py-3">
          <div class="flex items-end gap-2">
            <textarea
              class="flex-1 min-h-[2.75rem] max-h-40 px-3 py-2 rounded-md border border-input bg-transparent shadow-xs text-base sm:text-sm resize-none placeholder:text-muted-foreground focus-visible:outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
              placeholder={ready ? 'Mensagem para o agente…' : 'Selecione um endpoint e agente na sidebar'}
              value={input}
              disabled={!ready}
              onInput={(e) => setInput((e.target as HTMLTextAreaElement).value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              rows={1}
            />
            {turns.some((t) => t.pending) ? (
              <button
                class="size-11 sm:size-10 rounded-md bg-destructive text-destructive-foreground flex items-center justify-center shrink-0 hover:bg-destructive/90 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-destructive/40"
                onClick={stop}
                title="Interromper"
                aria-label="Interromper"
              >
                <i class="ph ph-square text-base" />
              </button>
            ) : (
              <button
                class="size-11 sm:size-10 rounded-md bg-primary text-primary-foreground flex items-center justify-center shrink-0 hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:opacity-50 disabled:pointer-events-none"
                onClick={send}
                disabled={!input.trim() || !ready}
                title="Enviar"
                aria-label="Enviar"
              >
                <i class="ph ph-paper-plane-tilt text-base" />
              </button>
            )}
          </div>
          {/* Dica de teclado só faz sentido em telas com keyboard físico */}
          <div class="text-[11px] text-muted-foreground mt-1.5 font-mono hidden sm:block">
            enter envia · shift+enter quebra linha
          </div>
        </div>
      </div>
    </div>
  );
}

// Editor inline pro user_id usado em `streamRun`. Antes vivia em /settings,
// mas só fazia sentido no contexto da conversa (cada agente/canal trata
// user_id de forma diferente). Persiste no mesmo localStorage de antes
// pra não quebrar quem já tinha valor configurado.
function UserIdEditor() {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(userId.value);

  useEffect(() => {
    if (editing) setDraft(userId.value);
  }, [editing]);

  const commit = () => {
    const v = draft.trim();
    if (!v) return;
    userId.value = v;
    setUserId(v);
    setEditing(false);
  };

  if (editing) {
    return (
      <div class="flex items-center gap-1">
        <span class="text-muted-foreground hidden sm:inline">como</span>
        <input
          class="h-8 w-40 rounded-md border border-input bg-transparent px-3 py-1 text-xs font-mono shadow-xs placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:border-ring"
          value={draft}
          onInput={(e) => setDraft((e.target as HTMLInputElement).value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape') setEditing(false);
          }}
          autoFocus
        />
        <button
          class="inline-flex items-center justify-center size-8 rounded-md text-success hover:bg-accent focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
          onClick={commit}
          title="Salvar"
          aria-label="Salvar"
        >
          <i class="ph ph-check text-sm" />
        </button>
      </div>
    );
  }

  return (
    <button
      class="inline-flex items-center justify-center gap-2 h-8 px-4 rounded-md text-xs font-medium font-mono bg-background text-muted-foreground border border-input shadow-xs hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:border-ring"
      onClick={() => setEditing(true)}
      title="Trocar user_id usado na conversa"
    >
      <i class="ph ph-user text-sm" />
      <span>como: {userId.value}</span>
    </button>
  );
}

function Toggle({
  label,
  icon,
  value,
  onChange,
  title,
}: {
  label: string;
  icon: preact.ComponentChildren;
  value: boolean;
  onChange: (v: boolean) => void;
  title?: string;
}) {
  return (
    <button
      class={`h-8 px-2.5 rounded-md border text-xs flex items-center gap-1.5 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 ${
        value
          ? 'border-border bg-muted text-foreground'
          : 'border-border text-muted-foreground hover:bg-accent hover:text-accent-foreground'
      }`}
      onClick={() => onChange(!value)}
      title={title}
    >
      {icon}
      <span>{label}</span>
      <span class={`size-1.5 rounded-full ${value ? 'bg-primary' : 'bg-muted-foreground/40'}`} />
    </button>
  );
}

// Routing dos eventos SSE → estrutura do turno.
//
// Em Team com members (modo coordinate), o supervisor emite `TeamRunContent`
// e cada subagent emite `RunContent`. ANTES estávamos concatenando ambos no
// mesmo bubble — daí a mistura. Agora:
//   - TeamRunContent (supervisor) → content principal (resposta final do usuário)
//   - RunContent (subagent)        → bucket separado por agent_name (subagents[X])
//   - IntermediateRunContent       → idem RunContent
function applyEvent(t: Turn, ev: AnyAgnoEvent): Turn {
  const name = ev.event;

  // ─── Conteúdo do supervisor — resposta final ─────────────────────────────
  if (name === 'TeamRunContent') {
    const e = ev as { content?: string };
    return { ...t, content: t.content + (e.content || '') };
  }

  // ─── Tool call concluída → injeta quebra de parágrafo no content do
  //     supervisor pra próximo delta não colar visualmente. Equivalente
  //     light do segmenter que o Salvador usa pro WhatsApp.
  //     Cai no fluxo padrão depois — o evento também vai pra timeline.
  if (name === 'TeamToolCallCompleted' || name === 'ToolCallCompleted') {
    if (t.content && !t.content.endsWith('\n\n')) {
      t = { ...t, content: t.content.replace(/\s*$/, '') + '\n\n' };
    }
  }

  // ─── Conteúdo de subagent — vira sub-bucket pra exibir se "passos" ON ────
  if (name === 'RunContent' || name === 'IntermediateRunContent') {
    const e = ev as { content?: string; agent_name?: string };
    const agent = e.agent_name || 'subagent';
    const prev = t.subagents[agent]?.content ?? '';
    return {
      ...t,
      subagents: {
        ...t.subagents,
        [agent]: { agent, content: prev + (e.content || '') },
      },
    };
  }

  // ─── Reasoning streaming (separa do content) ──────────────────────────────
  if (name === 'ReasoningStarted') {
    return { ...t, reasoningPending: true, reasoningStartedAt: Date.now() };
  }
  if (name === 'ReasoningContentDelta') {
    const e = ev as { reasoning_content?: string; content?: string };
    return { ...t, reasoning: t.reasoning + (e.reasoning_content || e.content || '') };
  }
  if (name === 'ReasoningStep') {
    const e = ev as { reasoning_content?: string; content?: string };
    const chunk = e.reasoning_content || e.content || '';
    return { ...t, reasoning: t.reasoning ? `${t.reasoning}\n\n${chunk}` : chunk };
  }
  if (name === 'ReasoningCompleted') {
    return { ...t, reasoningPending: false, reasoningEndedAt: Date.now() };
  }

  // ─── RunCompleted: captura métricas + content final ────────────────────────
  //
  // Em Team, cada member emite seu próprio `RunCompleted` — e ele chega ANTES
  // do supervisor ter escrito qualquer coisa. Sem a guarda de `parent_run_id`
  // abaixo, o `t.content || e.content` adotava a fala interna do subagent como
  // resposta do turno (e as métricas dele como métricas do turno); depois o
  // `TeamRunContent` do supervisor era concatenado ATRÁS dela. F5 "consertava"
  // porque o histórico já filtra runs de member (ver `runsToTurns`).
  // Discriminador: só run de member tem `parent_run_id` — no run do team e no
  // chat com agent solo ele é null, então o caminho normal segue valendo.
  if (name === 'TeamRunCompleted' || name === 'RunCompleted') {
    const e = ev as {
      content?: string;
      agent_name?: string;
      parent_run_id?: string | null;
      metrics?: {
        input_tokens?: number;
        output_tokens?: number;
        total_tokens?: number;
        time_to_first_token?: number;
        duration?: number;
        details?: { model?: Array<{ id: string; provider: string }> };
      };
    };

    // Fim do run de um member: fecha o bucket do subagent (fallback pro caso
    // de member que não streamou deltas) e NÃO toca content/metrics do turno.
    if (e.parent_run_id) {
      const agent = e.agent_name || 'subagent';
      const prev = t.subagents[agent]?.content ?? '';
      return {
        ...t,
        subagents: {
          ...t.subagents,
          [agent]: { agent, content: prev || e.content || '' },
        },
      };
    }

    return {
      ...t,
      content: t.content || e.content || '',
      metrics: e.metrics
        ? {
            inputTokens: e.metrics.input_tokens,
            outputTokens: e.metrics.output_tokens,
            totalTokens: e.metrics.total_tokens,
            timeToFirstToken: e.metrics.time_to_first_token,
            duration: e.metrics.duration,
            models: e.metrics.details?.model,
          }
        : t.metrics,
    };
  }

  // ─── Eventos "ruído" que não vão pra timeline (lista em NOISE_EVENTS) ─────
  if (NOISE_EVENTS.has(name)) return t;

  // ─── Tudo mais → vai pra timeline (tool calls, delegation, memory, hooks) ─
  return { ...t, events: [...t.events, ev] };
}
